import {inject, injectable, multiInject} from "inversify";
import {Incident, Match} from "@/modules/match/entity";
import AbstractMatchEvaluator from "@/modules/competitions/services/evaluators/AbstractMatchEvaluator";
import {waitMinimumFor} from "@/utils";
import {HomeAwayType, LiveIncidentType, TickType, TYPES} from "@/modules/match/types";
import {Player, Team} from "@/modules/team/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import LineupSkillCalculator from "@/modules/team/services/LineupSkillCalculator";
import EffectiveLineupSkillCalculator from "@/modules/team/services/EffectiveLineupSkillCalculator";
import {LiveMatchHandlerInterface} from "@/modules/match/interfaces";
import {EventEmitter} from "@/modules/base/events";
import {LineupEnum} from "@/modules/base/enums";

@injectable()
export default class LiveMatchEvaluator extends AbstractMatchEvaluator {
  private match?: Match;
  private onTick?: (tick: TickType) => Promise<void>;
  private onIncident?: (incident: LiveIncidentType) => Promise<void>;
  private onFinished?: (match: Match) => Promise<void>;
  private onPauseChange?: (tick: TickType) => Promise<void>;
  private tick: TickType;
  private minute = 0;
  private minimumMinuteDurationMS = 500;
  public isPaused = false;
  private shouldEnd = false;
  private isPausedBeforeInteraction: boolean;

  public substitutionLeft = 5;
  public suspendedPlayerIds: number[] = [];

  public homePlayers: Player[];
  public awayPlayers: Player[];
  private incidents: Incident[] = [];

  private startLineupMapping: { [key in HomeAwayType]: { [key: number]: LineupEnum } } = {home: {}, away: {}};
  private substitutedPlayerIds: number[] = [];

  static nextMatch?: Match | null;

  constructor(
    @inject(EventEmitter) public em: EventEmitter,
    @inject(ConfigRepository) configRepository: ConfigRepository,
    @inject(LineupSkillCalculator) lineupSkillCalculator: LineupSkillCalculator,
    @inject(EffectiveLineupSkillCalculator) effectiveLineupSkillCalculator: EffectiveLineupSkillCalculator,
    @multiInject(TYPES.LiveMatchHandlers) private liveMatchHandlers: LiveMatchHandlerInterface[],
  ) {
    super(configRepository, lineupSkillCalculator, effectiveLineupSkillCalculator);
  }

  getHandler(handler: (...args: any[]) => LiveMatchHandlerInterface) {
    return this.liveMatchHandlers.find(h => h instanceof handler);
  }

  getIncidents() {
    return this.incidents;
  }

  end() {
    this.shouldEnd = true;

    return this;
  }

  pause() {
    this.isPaused = true;

    if (this.onPauseChange) {
      this.onPauseChange(this.tick);
    }

    return this;
  }

  unpause() {
    this.isPaused = false;

    if (this.onPauseChange) {
      this.onPauseChange(this.tick);
    }

    return this;
  }

  setSlow() {
    this.minimumMinuteDurationMS = 500;

    return this;
  }

  setFast() {
    this.minimumMinuteDurationMS = 0;

    return this;
  }

  setOnTick(callback: (tick: TickType) => Promise<void>) {
    this.onTick = callback;

    return this;
  }

  setOnPauseChange(callback: (tick: TickType) => Promise<void>) {
    this.onPauseChange = callback;

    return this;
  }

  setOnIncident(callback: (incident: LiveIncidentType) => Promise<void>) {
    this.onIncident = callback;

    return this;
  }

  setOnFinished(callback: (match: Match) => Promise<void>) {
    this.onFinished = callback;

    return this;
  }

  public async triggerIncident(liveIncident: LiveIncidentType) {
    this.incidents.push(liveIncident.incident);

    if (this.onIncident) {
      await this.onIncident(liveIncident);
    }

    await collect(this.liveMatchHandlers)
      .sortByDesc((h: LiveMatchHandlerInterface) => h.priority())
      .toArray()
      .forEachAsync((h: LiveMatchHandlerInterface) => h.onIncident(this, liveIncident));
  }

  private async triggerTick(tick: TickType) {
    await collect(this.liveMatchHandlers)
      .sortByDesc((h: LiveMatchHandlerInterface) => h.priority())
      .toArray()
      .forEachAsync(async (h: LiveMatchHandlerInterface) => {
        if (!this.shouldEnd) {
          await h.onTick(this, tick);
        }
      });
  }

  protected async prepare(match: Match): Promise<void> {
    await super.prepare(match);

    this.homePlayers = match.homeTeam.players;
    this.awayPlayers = match.awayTeam.players;

    this.homePlayers.filter(p => p.lineup).forEach(p => this.startLineupMapping.home[p.id] = p.lineup!);
    this.awayPlayers.filter(p => p.lineup).forEach(p => this.startLineupMapping.away[p.id] = p.lineup!);
  }

  public needDecision?: boolean;
  public firstLegMatch?: Match;

  setNeedDecision(needDecision: boolean, firstLegMatch?: Match) {
    this.needDecision = needDecision;
    this.firstLegMatch = firstLegMatch;

    return this;
  }

  async evaluate(match: Match): Promise<void> {
    this.match = match;
    await this.prepare(match);

    while (!this.shouldEnd) {
      while (this.isPaused) {
        await waitFor(1000);
      }

      this.tick = {
        evaluator: this,
        match,
        minute: this.minute,
        homePlayers: this.homePlayers,
        awayPlayers: this.awayPlayers,
        incidents: this.incidents,
      };

      await waitMinimumFor(this.minimumMinuteDurationMS, async () => {
        await this.triggerTick(this.tick);

        if (this.onTick) {
          await this.onTick!(this.tick);
        }
      });

      if (!this.shouldEnd) {
        this.minute++;
      }
    }

    await this.processResult(match);

    if (this.onFinished) {
      await this.onFinished(match);

      await this.match.homeTeam.players.forEachAsync(async (p: Player) => {
        p.lineup = this.startLineupMapping.home[p.id] || null;
        await p.save();
      });
      await this.match.awayTeam.players.forEachAsync(async (p: Player) => {
        p.lineup = this.startLineupMapping.away[p.id] || null;
        await p.save();
      });
    }

    await collect(this.liveMatchHandlers)
      .sortByDesc((h: LiveMatchHandlerInterface) => h.priority())
      .toArray()
      .forEachAsync((h: LiveMatchHandlerInterface) => h.onFinish(this, this.tick));
  }

  async startInteraction() {
    this.isPausedBeforeInteraction = this.isPaused;
    this.isPaused = true;
  }

  async endInteraction() {
    if (!this.isPausedBeforeInteraction) {
      this.isPaused = false;
    }
  }

  public createLiveIncident = (incident: Incident): LiveIncidentType => ({
    incident,
    evaluator: this,
    match: this.match!,
    minute: this.tick ? this.tick.minute : 1,
    homePlayers: this.homePlayers,
    awayPlayers: this.awayPlayers,
    incidents: this.incidents,
  });

  async substitute(team: Team, playerIn: Player, playerOut: Player) {
    if (this.substitutionLeft <= 0) {
      throw 'There are no more substitutions left.';
    }

    playerIn.lineup = playerOut.lineup;
    playerOut.lineup = null;
    this.substitutedPlayerIds.push(playerOut.id);

    await (await this.createSubstituteIncident(this.match!, team, playerIn, playerOut))
      .forEachAsync(async (incident: Incident) => {
        await this.triggerIncident(
          this.createLiveIncident(incident)
        );
      });

    this.substitutionLeft--;
  }

  protected getMinute(): number {
    return this.tick ? this.tick.minute : 1;
  }
}
